/** The What's new query as every reader asks for it: up to the build
 *  this tab runs, in the UI language. The controller runs it; the
 *  navbar and the release-notes page read the same cache entry. */
import * as React from "react";
import { useTranslation } from "react-i18next";

import { useAppVersion, useMarkReleaseNotesSeen, useMe, useWhatsNew } from "@/api/hooks";
import { tabBuild } from "@/api/loadedBuild";
import { serverStatusFrom } from "@/api/serverStatus";
import { postSeen } from "@/features/whatsNew/channel";
import { whatsNewGate } from "@/features/whatsNew/logic";
import { useBrowserOnline, usePageVisible } from "@/hooks/useBrowserPresence";

/** ``whatsNewGate`` over the live state: the user and the server are
 *  known, the tab runs the server's build (no reload pending), no deploy
 *  is under way, the backend and the network are there, and the tab is
 *  visible. Both the dialog and the release-notes page acknowledge only
 *  through it, so a stale tab never acknowledges. */
export function useWhatsNewGate(): boolean {
  const me = useMe();
  const version = useAppVersion();
  const online = useBrowserOnline();
  const visible = usePageVisible();
  const status = serverStatusFrom(version);
  const maintenance = status.maintenance ?? version.data?.maintenance ?? null;
  return whatsNewGate({
    meReady: me.isSuccess,
    versionReady: version.isSuccess,
    tabVersion: tabBuild()?.build.version ?? null,
    updateAvailable: status.updateAvailable,
    unreachable: status.unreachable,
    strictMaintenance: Boolean(maintenance?.enabled && maintenance.strict),
    online,
    visible,
  });
}

export function useWhatsNewForTab({ enabled }: { enabled: boolean }) {
  const { i18n } = useTranslation();
  return useWhatsNew({
    through: tabBuild()?.build.version ?? null,
    lang: i18n.resolvedLanguage ?? "en",
    enabled,
  });
}

/** How many changes the user hasn't been offered yet (the navbar dot);
 *  0 until the controller has asked, and after an acknowledgement. */
export function useWhatsNewUnread(): number {
  const { data } = useWhatsNewForTab({ enabled: false });
  if (!data?.index_ok) return 0;
  return data.notes.length + data.omitted;
}

/** Acknowledge a version: POST it, clear the cached unread notes, and
 *  tell the other tabs. A failure is ignored: the next page load offers
 *  the same notes again (at least once, never lost). */
export function useAcknowledgeWhatsNew(): (version: string) => void {
  const { mutate } = useMarkReleaseNotesSeen({ onSeen: postSeen });
  return React.useCallback((version: string) => mutate(version), [mutate]);
}
